import { pgTable, text, serial, integer, bigint, numeric, timestamp, real, boolean, jsonb, customType } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Custom bytea type for binary data
const bytea = customType<{ data: Buffer; notNull: false; default: false }>({
  dataType() {
    return "bytea";
  },
  toDriver(value: Buffer): Buffer {
    return value;
  },
  fromDriver(value: unknown): Buffer {
    if (Buffer.isBuffer(value)) {
      return value;
    }
    if (value instanceof Uint8Array) {
      return Buffer.from(value);
    }
    return Buffer.from(value as string, 'hex');
  },
});

// Users table (keeping original table for compatibility)
export const users = pgTable("users", {
  id: serial("id").primaryKey(),
  username: text("username").notNull().unique(),
  password: text("password").notNull(),
});

export const insertUserSchema = createInsertSchema(users).pick({
  username: true,
  password: true,
});

export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;

// Positions-Kalkulation (Tiefen-/Blitzkalkulation)
// Eine Kalkulationszeile gehört zu genau einer Kategorie:
// - lohn:      menge = Zeit (Einheit siehe zeitEinheit), einzelpreis = Stundensatz (€/h)
// - material:  menge = Stückzahl (default 1), einzelpreis = EK-Preis, aufschlagProzent optional (sonst Standard)
// - sonstiges: menge = Stückzahl (default 1), einzelpreis = Betrag (darf negativ sein, z.B.
//              automatisch eingefügte Rabatt-Zeile aus dem Gesamtaufschlag der Blitzkalkulation)
// Herkunft eines Zeilen-PREISES (Snapshot-Pflicht Live-Preis, Review-Auflage 2026-08):
// wird ein Live-Preis (Händler-API, live_price_quotes) in eine Kalkulationszeile
// übernommen, friert die Zeile ZUSÄTZLICH zum Betrag die Herkunft ein, damit Angebote
// historisch stabil bleiben (ein späterer Live-Preis ändert die Zeile NICHT rückwirkend).
// ADDITIV wie mengeHerkunftSchema — Bestand ohne das Feld bleibt byte-gleich gültig.
// Wird der Betrag (einzelpreis) manuell geändert, LÖSCHT der Editierpfad den Vermerk
// (shared/kalkulation.ts::setzeBlitzZeile / wendeZeilenUpdateMitHerkunftsSchutzAn) —
// ein Herkunftsvermerk nach Handänderung würde lügen (Muster mengeHerkunft).
export const preisHerkunftSchema = z.object({
  quelle: z.literal('live'),
  // Lieferanten-Kennung (Softecs suppliers.id) — die App B kennt hier nur die ID,
  // keinen Anzeigenamen; ehrlich die ID einfrieren statt einen Namen zu raten.
  lieferant: z.string(),
  quoteId: z.string(),
  // ISO-Zeitstempel des Live-Abrufs (fetchedAt des Quotes)
  fetchedAt: z.string(),
  // Preiseinheit des Quotes im Katalog-Format (z.B. "100 M", "ST") — dokumentiert,
  // WORAUF sich der eingefrorene Betrag ursprünglich bezog.
  priceUnit: z.string(),
  // Divisor N, mit dem der Quote-Preis auf EINE Einheit umgerechnet wurde
  // (shared/effective-unit-price.ts::effectiveUnitPrice, Feld n)
  faktor: z.number(),
});

export type PreisHerkunft = z.infer<typeof preisHerkunftSchema>;

export const kalkulationsZeileSchema = z.object({
  id: z.string(),
  art: z.enum(['lohn', 'material', 'sonstiges']),
  beschreibung: z.string().optional(),
  menge: z.number().min(0).optional(),
  // Zeiteinheit der Lohnzeilen-Menge; Default 'h' (Bestand ohne Feld = 'h')
  zeitEinheit: z.enum(['h', 'min']).optional(),
  einzelpreis: z.number().optional(),
  aufschlagProzent: z.number().min(0).optional(),
  // Snapshot-Herkunft des einzelpreis (Live-Preis-Übernahme) — additiv + optional,
  // siehe preisHerkunftSchema oben.
  preisHerkunft: preisHerkunftSchema.nullable().optional(),
});

export type KalkulationsZeile = z.infer<typeof kalkulationsZeileSchema>;

// mengenBezug steuert, worauf sich die Kalk-Summe bezieht:
// - 'einheit': Kalkulation gilt je Einheit ⇒ EP = gesamtNetto (Standard für NEUE Kalkulationen)
// - 'gesamt':  Kalkulation gilt für die gesamte Positionsmenge ⇒ EP = gesamtNetto / Menge
//              (Standard für Bestandsdaten ohne dieses Feld — bisheriges Verhalten, keine
//              stillen Preisänderungen an gespeicherten Angeboten!)
export const positionsKalkulationSchema = z.object({
  zeilen: z.array(kalkulationsZeileSchema),
  mengenBezug: z.enum(['einheit', 'gesamt']).optional(),
  // Overrides pro Position; wenn nicht gesetzt, gelten die globalen Defaults
  stundensatz: z.number().min(0).optional(),
  materialAufschlagProzent: z.number().min(0).optional(),
  notiz: z.string().optional(),
});

export type PositionsKalkulation = z.infer<typeof positionsKalkulationSchema>;

// Aufmaß: Einzelaufmaß einer LV-Position. Mehrere Zeilen kumulieren zur Ist-Menge;
// Restmenge = Soll-Menge (LV) minus Summe der Ist-Mengen. Datum = ISO-Datum (YYYY-MM-DD).
export const aufmassZeileSchema = z.object({
  id: z.string(),
  datum: z.string().optional(),
  menge: z.number(),
  notiz: z.string().optional(),
  // true = bereits in einer Rechnung abgerechnet (nicht erneut übernehmen)
  abgerechnet: z.boolean().optional(),
});

export type AufmassZeile = z.infer<typeof aufmassZeileSchema>;

export const positionsAufmassSchema = z.object({
  zeilen: z.array(aufmassZeileSchema),
});

export type PositionsAufmass = z.infer<typeof positionsAufmassSchema>;

// Herkunft einer Menge (Bauteilbezug): aus welcher Quelle wurde die Menge
// übernommen? Aktuell nur IFC-Modellimport. ADDITIV — Bestand ohne das Feld
// bleibt byte-gleich gültig. Wird die Menge manuell geändert, LÖSCHT
// updateQuantity den Vermerk (client/src/lib/store.ts) — ein Herkunftsvermerk
// nach Handänderung würde lügen.
export const mengeHerkunftSchema = z.object({
  quelle: z.literal('ifc'),
  // Dateiname des IFC-Modells (keine Pfade — die Datei bleibt lokal im Browser)
  datei: z.string(),
  // Wie viele Bauteile trugen zur Gruppe bei
  elementAnzahl: z.number(),
  // Gewählte Mengenart (lesbares Label, z. B. "Fläche (netto)")
  mengenart: z.string(),
  // Ehrlichkeits-Kennzahl: Elemente OHNE gespeicherten Wert dieser Mengenart
  elementeOhneWert: z.number(),
});

export type MengeHerkunft = z.infer<typeof mengeHerkunftSchema>;

// Herkunft einer ganzen POSITION: aus einem anderen gespeicherten Angebot
// übernommen ("Positionen aus anderem Angebot", Wunsch Malermeister).
// Der Vermerk dokumentiert vor allem den PREISSTAND — der Preis stammt aus
// einem älteren Angebot und ist nicht zwingend aktuell. ADDITIV wie
// mengeHerkunftSchema: Bestand ohne das Feld bleibt byte-gleich gültig.
// Wird der Preis manuell geändert, LÖSCHT updatePosition den Vermerk
// (client/src/lib/store.ts) — ein Herkunftsvermerk nach Handänderung würde
// lügen (Muster mengeHerkunft / preisHerkunft).
export const positionsHerkunftSchema = z.object({
  quelle: z.literal('angebot'),
  // Angebotsnummer des Quell-Angebots (header.angebot_nr)
  angebotNr: z.string(),
  // Angebotsdatum des Quell-Angebots (ISO-Datum) — der "Preisstand"
  datum: z.string().optional(),
  kunde: z.string().optional(),
  // Slot des Quell-Angebots (kann inzwischen gelöscht/überschrieben sein —
  // nur Komfort zum Wiederfinden, keine harte Referenz)
  slot: z.number().optional(),
});

export type PositionsHerkunft = z.infer<typeof positionsHerkunftSchema>;

// Position type (standalone — the positions pgTable was removed as dead code)
export type Position = {
  id: number;
  originalId: string | null;
  positionNumber?: string | null;
  gewerk: string | null;
  // F2: Untertitel (Ebene 2) unterhalb des Gewerks/Titels. Struktur lebt als Attribut
  // auf der Zeile (keine Header-Zeilen!) — siehe shared/quote-numbering.ts.
  untergewerkName?: string | null;
  name: string | null;
  beschreibung: string;
  // GAEB-Langtext (Wunsch Malermeister): getrennt vom Kurztext (name) und der
  // bisherigen beschreibung. Bestandsschutz: beschreibung behält ihre Bedeutung,
  // longText ist rein additiv (leer, wenn die Quelle nur einen Text liefert).
  longText?: string | null;
  einheit: string;
  preis: number;
  // true = "kein Preis erfasst" (z.B. Import mit abgewähltem "Preise mit importieren").
  // preis bleibt dann numerisch 0, aber Ausgabewege drucken "–" statt 0,00 €.
  // Unterscheidet fehlenden Preis von echten 0,00-€-Positionen (Bedarfs-/Alternativ-
  // positionen mit realem Nullpreis existieren zu Hunderten in echten LVs).
  preisFehlt?: boolean;
  umsatzsteuer: number;
  budgetTaskId?: string | null;
  kalkulation?: PositionsKalkulation | null;
  aufmass?: PositionsAufmass | null;
  // Positionsart (F1): zählt nicht in die Auftrags-Hauptsumme (shared/quote-totals.ts)
  isBedarfsposition?: boolean;
  isAlternativposition?: boolean;
  // Bauteilbezug: Menge stammt aus einem IFC-Modell (siehe mengeHerkunftSchema)
  mengeHerkunft?: MengeHerkunft | null;
  // Übernommen aus einem anderen Angebot (siehe positionsHerkunftSchema);
  // updatePosition löscht den Vermerk bei manueller Preisänderung.
  positionsHerkunft?: PositionsHerkunft | null;
};

// Quote header schema
export const quoteHeaderSchema = z.object({
  kunde: z.string().min(1, "Kunde ist erforderlich"),
  anschrift: z.string().optional(),
  adresszusatz: z.string().optional(), // Additional address info (c/o, department, etc.)
  angebot_nr: z.string().min(1, "Angebotsnummer ist erforderlich"),
  datum: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: "Gültiges Datum erforderlich",
  }),
  bauvorhaben: z.string().optional(),
  gewerk: z.string().optional(),
  auftrag: z.string().optional(),
  eingereichte_unterlagen: z.string().optional(),
  lz: z.string().optional(),
  mwstSatz: z.union([z.literal(0), z.literal(7), z.literal(19)]).optional(),
  vortext: z.string().optional(), // Individueller Vortext vor den Positionen (ersetzt Standardgruss im PDF)
  schlusstext: z.string().optional(), // Individueller Schlusstext nach der Summen-/Schlusssektion im PDF
  companyProfileId: z.string().optional(), // Firmen-Briefkopf-Profil (z.B. Fortuna Elektro vs. Fortuna Bautec); leer = Standardprofil
  // Textfassung fürs Dokument (Kurz-/Langtext-Umschalter, EINE Einstellung je Dokument):
  // 'kurztext'  → PDF druckt name (Kurztext), Fallback beschreibung
  // 'langtext'  → PDF druckt longText (Langtext), Fallback beschreibung
  // undefined   → bisheriges Verhalten: beschreibung (Bestandsschutz, byte-gleiche PDFs)
  textFassung: z.enum(['kurztext', 'langtext']).optional(),
  // Nachtrag: Angebotsnummer des Basisangebots (Nummer dieses Angebots ist dann
  // "<nachtragZu>/N<n>", siehe shared/quote-number-circle.ts). Additiv/optional —
  // Bestand ohne das Feld bleibt unverändert gültig.
  nachtragZu: z.string().optional(),
  // Slot (stabile Kennung) des Basisangebots — macht die Nachtrags-Verknüpfung
  // eindeutig, wenn dieselbe Angebotsnummer mehrfach vergeben ist. Additiv:
  // Bestands-Nachträge tragen nur nachtragZu (Nummer) und bleiben gültig;
  // die Nummer bleibt für Anzeige/Dokument maßgeblich.
  nachtragZuSlot: z.number().int().positive().optional(),
});

export type QuoteHeader = z.infer<typeof quoteHeaderSchema>;

// Quote item schema
export const quoteItemSchema = z.object({
  id: z.number(),
  originalId: z.string().optional(),
  positionNumber: z.string().optional(), // Variable depth LV-Nr (e.g., "1.1.1", "01.02.003")
  name: z.string().optional(),
  beschreibung: z.string(),
  // GAEB-Langtext, getrennt vom Kurztext (name). Additiv: Quellen mit nur einem
  // Text lassen das Feld leer (NICHT raten/umverteilen — beschreibung bleibt wie sie ist).
  longText: z.string().optional(),
  gewerk: z.string().optional(),
  einheit: z.string(),
  preis: z.number(),
  // true = "kein Preis erfasst" (Import ohne Preise). preis bleibt 0, Ausgabewege
  // drucken "–" statt 0,00 € — sonst wären fehlende Preise von echten
  // 0,00-€-Positionen (Bedarfs-/Alternativpositionen) ununterscheidbar.
  preisFehlt: z.boolean().optional(),
  umsatzsteuer: z.number(),
  menge: z.number().min(0.01).refine(
    (val) => Number(val.toFixed(2)) === val || Number(val.toFixed(2)) === Math.floor(val * 100) / 100,
    { message: "Maximal 2 Dezimalstellen erlaubt" }
  ),
  rabattTyp: z.enum(['percent', 'absolute']).optional(),
  rabattWert: z.number().min(0).optional(),

  // Positionsart: Bedarfs-/Eventual- bzw. Alternativposition. Zählt NICHT in die
  // Auftrags-Hauptsumme (siehe shared/quote-totals.ts, isExcludedFromMainSum).
  isBedarfsposition: z.boolean().optional(),
  isAlternativposition: z.boolean().optional(),

  // GAEB hierarchy fields (for proper Softec mapping)
  gewerkNr: z.string().optional(),           // "01" - main trade number for Softec Trade
  gewerkName: z.string().optional(),         // "Starkstromanlagen" - main trade name
  untergewerkNr: z.string().optional(),      // "01.01" - sub-trade number
  untergewerkName: z.string().optional(),    // "Eigenstromversorgung" - sub-trade name
  isHeader: z.boolean().optional(),          // true for Untergewerk title entries (no quantity/price)
  parentPositionNumber: z.string().optional(), // Reference to parent for Softec parentTaskId
  hierarchyLevel: z.number().optional(),     // Depth: 0=Gewerk, 1=Untergewerk, 2+=Position
  budgetTaskId: z.string().optional(),       // Optional Herzstück task link for invoice sync
  kalkulation: positionsKalkulationSchema.optional(), // Tiefen-/Blitzkalkulation der Position
  aufmass: positionsAufmassSchema.optional(),         // Aufmaß / Mengenfortschreibung der Position
  // Bauteilbezug: Menge aus IFC übernommen. Additiv + optional — Bestand ohne
  // das Feld parst unverändert. updateQuantity löscht es bei Handänderung.
  mengeHerkunft: mengeHerkunftSchema.nullable().optional(),
  // Übernommen aus einem anderen Angebot (Preisstand-Vermerk). Additiv +
  // optional; updatePosition löscht es bei manueller Preisänderung.
  positionsHerkunft: positionsHerkunftSchema.nullable().optional(),
});

export type QuoteItem = z.infer<typeof quoteItemSchema>;

// Excel row validation schema
export const excelRowSchema = z.object({
  id: z.union([z.string(), z.number()]).optional(),
  gewerk: z.string().optional(),
  name: z.string().optional(),
  beschreibung: z.string().min(1, "Beschreibung ist erforderlich"),
  einheit: z.string().min(1, "Einheit ist erforderlich"),
  preis: z.union([
    z.number().min(0, "Preis muss positiv sein"),
    z.string().transform((val) => {
      const parsed = parseFloat(val.replace(',', '.'));
      if (isNaN(parsed)) throw new Error("Ungültiger Preis");
      return parsed;
    }),
  ]),
  umsatzsteuer: z.union([
    z.number().int().min(0, "MwSt muss mindestens 0% sein"),
    z.string().transform((val) => {
      const parsed = parseInt(val);
      if (isNaN(parsed)) throw new Error("Ungültige MwSt");
      return parsed;
    }),
  ]),
});

export type ExcelRow = z.infer<typeof excelRowSchema>;

// ============================================
// PostgreSQL Backup Tables
// Diese Tabellen dienen als ZUSÄTZLICHE Sicherheitskopie
// JSON-Dateien bleiben die primäre Datenquelle!
// ============================================

// Tabelle 1: GAEB-Datenbanken Backup
export const databaseBackups = pgTable("database_backups", {
  id: text("id").primaryKey(),
  name: text("name").notNull(),
  type: text("type").notNull(),
  jsonData: text("json_data").notNull(),
  createdAt: timestamp("created_at").notNull(),
  updatedAt: timestamp("updated_at").notNull(),
});

export type DatabaseBackup = typeof databaseBackups.$inferSelect;
export type InsertDatabaseBackup = typeof databaseBackups.$inferInsert;

// Tabelle 2: Angebote/Quotes Backup
export const quoteBackups = pgTable("quote_backups", {
  id: text("id").primaryKey(),
  slot: integer("slot").notNull(),
  jsonData: text("json_data").notNull(),
  createdAt: timestamp("created_at").notNull(),
  updatedAt: timestamp("updated_at").notNull(),
});

export type QuoteBackup = typeof quoteBackups.$inferSelect;
export type InsertQuoteBackup = typeof quoteBackups.$inferInsert;

// Tabelle 3: Einstellungen Backup (Logo, PDF-Config, etc.)
export const settingsBackups = pgTable("settings_backups", {
  key: text("key").primaryKey(),
  jsonData: text("json_data"),
  binaryData: bytea("binary_data"),
  mimeType: text("mime_type"),
  updatedAt: timestamp("updated_at").notNull(),
});

export type SettingsBackup = typeof settingsBackups.$inferSelect;
export type InsertSettingsBackup = typeof settingsBackups.$inferInsert;

// ============================================
// Rechnungswesen (Invoice System)
// PostgreSQL ist hier PRIMÄRE Datenquelle (nicht JSON-Backup!)
// Compliance erfordert Audit Trail + atomare Nummernvergabe
// ============================================

// JSONB-Typen für Rechnungen
export type InvoiceDeduction = {
  invoiceId: string;
  invoiceNumber: string;
  amount: number;
  description: string;
};

export type InvoiceDiscount = {
  id: string;
  type: 'percentage' | 'fixed' | 'skonto';
  label: string;
  value: number;
  paymentTermDays?: number;
};

export type InvoiceAuditEntry = {
  timestamp: string;
  action: string;
  user: string;
  details: string;
  previousValue?: unknown;
  newValue?: unknown;
};

// Rechnungen (Haupttabelle)
export const invoices = pgTable("invoices", {
  id: text("id").primaryKey(),
  companyId: text("company_id"),                     // Softec multi-tenancy (NOT NULL in Softec schema)

  // Projekt- und Kundenzuordnung
  softecProjectId: text("softec_project_id"),       // FK zum Softec-Projekt
  softecClientId: text("softec_client_id"),          // FK zum Softec-Kunden
  clientName: text("client_name"),                   // Denormalisiert für Anzeige
  clientAddress: text("client_address"),
  projectName: text("project_name"),                 // Denormalisiert

  // Firmen-Briefkopf-Profil (z.B. Fortuna Elektro vs. Fortuna Bautec), vom
  // Angebot übernommen (Codex Runde 8, Finding 1). NULL = Legacy/kein
  // Profil gewählt -> Fallback auf das aktuell aktive Profil (siehe
  // resolveCompanyProfile in pdf-settings-storage.ts). Wird bei
  // Finalisierung in den sellerSnapshot eingefroren und danach nicht mehr
  // ausgewertet.
  companyProfileId: text("company_profile_id"),

  // Rechnungstyp und Status
  type: text("type").notNull().default("standard"),  // standard | partial | final | advance | storno
  status: text("status").notNull().default("draft"), // draft | finalized | sent | paid | partially_paid | cancelled
  partialNumber: integer("partial_number"),           // Laufende Nr. bei Abschlagsrechnungen (1, 2, 3...)

  // Nummern und Daten
  // Vergeben bei Finalisierung (Format NNN-YYYY, z.B. 001-2027, siehe invoice_counters.format_pattern).
  // GoBD: Eindeutigkeit wird per partiellem Unique-Index in server/db.ts (ensureDbSchema)
  // durchgesetzt: CREATE UNIQUE INDEX ... ON invoices(invoice_number) WHERE invoice_number IS NOT NULL.
  invoiceNumber: text("invoice_number"),
  draftNumber: text("draft_number"),                  // Interner Entwurfs-Identifier
  invoiceDate: timestamp("invoice_date"),             // Rechnungsdatum (gesetzt bei Finalisierung)
  dueDate: timestamp("due_date"),                     // Fälligkeitsdatum
  deliveryDate: timestamp("delivery_date"),           // Leistungsdatum

  // Beträge
  netTotal: numeric("net_total", { precision: 12, scale: 2 }).notNull().default("0").$type<number>(),
  vatTotal: numeric("vat_total", { precision: 12, scale: 2 }).notNull().default("0").$type<number>(),
  grossTotal: numeric("gross_total", { precision: 12, scale: 2 }).notNull().default("0").$type<number>(),

  // Zahlungsstatus
  paidAmount: numeric("paid_amount", { precision: 12, scale: 2 }).notNull().default("0").$type<number>(),
  paidAt: timestamp("paid_at"),

  // Texte
  title: text("title"),
  introText: text("intro_text"),
  outroText: text("outro_text"),
  paymentTerms: text("payment_terms"),
  internalNotes: text("internal_notes"),
  // Kurz-/Langtext-Umschalter je Rechnung (Wunsch Malermeister):
  // 'kurztext' | 'langtext' | NULL. NULL = bisheriges Verhalten (title, sonst
  // description). 'langtext' druckt longDescription (Fallback description/title).
  // Persistiert auf der Rechnung, damit finalisierte Rechnungen reproduzierbar
  // bleiben (GoBD).
  textFassung: text("text_fassung"),

  // AZ-Kette (für kumulative Abrechnung)
  previousInvoiceIds: jsonb("previous_invoice_ids").$type<string[]>().default([]),
  deductions: jsonb("deductions").$type<InvoiceDeduction[]>().default([]),
  discounts: jsonb("discounts").$type<InvoiceDiscount[]>().default([]),

  // Audit Trail
  auditTrail: jsonb("audit_trail").$type<InvoiceAuditEntry[]>().default([]),

  // PDF
  pdfPath: text("pdf_path"),
  pdfGeneratedAt: timestamp("pdf_generated_at"),

  // E-Rechnung
  zugferdXmlPath: text("zugferd_xml_path"),
  xrechnungXmlPath: text("xrechnung_xml_path"),

  // GoBD: Seller-Snapshot bei Finalisierung (reproduzierbare PDFs)
  sellerSnapshot: jsonb("seller_snapshot"),

  // ZUGFeRD-Pflichtfelder
  buyerReference: text("buyer_reference"),
  contractReference: text("contract_reference"),
  projectReference: text("project_reference"),

  // Erstellt von (SSO-User oder "system")
  createdBy: text("created_by"),
  finalizedBy: text("finalized_by"),
  finalizedAt: timestamp("finalized_at"),

  // Timestamps
  createdAt: timestamp("created_at").notNull(),
  updatedAt: timestamp("updated_at").notNull(),

  // Soft delete
  isDeleted: boolean("is_deleted").default(false),
  deletedAt: timestamp("deleted_at"),
  deletedBy: text("deleted_by"),
});

export type Invoice = typeof invoices.$inferSelect;
export type NewInvoice = typeof invoices.$inferInsert;

// Rechnungspositionen
export const invoiceItems = pgTable("invoice_items", {
  id: text("id").primaryKey(),
  invoiceId: text("invoice_id").notNull().references(() => invoices.id, { onDelete: "cascade" }),

  // Position
  position: integer("position").notNull(),            // Sortierung
  positionNumber: text("position_number"),             // LV-Nr (z.B. "01.01.001")

  // Inhalt
  title: text("title"),
  description: text("description"),
  longDescription: text("long_description"),

  // Mengen und Preise
  quantity: numeric("quantity", { precision: 10, scale: 4 }).notNull().default("0").$type<number>(),
  unit: text("unit"),
  unitPrice: numeric("unit_price", { precision: 12, scale: 2 }).notNull().default("0").$type<number>(),

  // Berechnete Beträge
  netAmount: numeric("net_amount", { precision: 12, scale: 2 }).notNull().default("0").$type<number>(),
  vatRate: numeric("vat_rate", { precision: 5, scale: 2 }).notNull().default("19").$type<number>(),
  vatAmount: numeric("vat_amount", { precision: 12, scale: 2 }).notNull().default("0").$type<number>(),
  grossAmount: numeric("gross_amount", { precision: 12, scale: 2 }).notNull().default("0").$type<number>(),

  // Rabatt pro Position
  discountPercent: numeric("discount_percent", { precision: 5, scale: 2 }).$type<number>(),
  discountAmount: numeric("discount_amount", { precision: 12, scale: 2 }).$type<number>(),

  // Budget-Verknüpfung (für Leistungsstand)
  budgetTaskId: text("budget_task_id"),                // FK zum Softec budgetTask
  cumulativePercent: numeric("cumulative_percent", { precision: 5, scale: 2 }).$type<number>(),

  // Typ
  type: text("type").default("service"),               // service | material | flat_rate | deduction

  // ZUGFeRD
  sellerItemId: text("seller_item_id"),
  buyerItemId: text("buyer_item_id"),
  standardItemId: text("standard_item_id"),
});

export type InvoiceItem = typeof invoiceItems.$inferSelect;
export type NewInvoiceItem = typeof invoiceItems.$inferInsert;

// Zahlungseingänge
export const invoicePayments = pgTable("invoice_payments", {
  id: text("id").primaryKey(),
  invoiceId: text("invoice_id").notNull().references(() => invoices.id, { onDelete: "cascade" }),

  // Zahlung
  amount: numeric("amount", { precision: 12, scale: 2 }).notNull().$type<number>(),
  paymentDate: timestamp("payment_date").notNull(),
  paymentMethod: text("payment_method"),               // bank_transfer | cash | check | other
  reference: text("reference"),                        // Bankref / Verwendungszweck
  notes: text("notes"),

  // Erstellt von
  createdBy: text("created_by"),
  createdAt: timestamp("created_at").notNull(),
});

export type InvoicePayment = typeof invoicePayments.$inferSelect;
export type NewInvoicePayment = typeof invoicePayments.$inferInsert;

// Atomare Nummernvergabe (pro Jahr)
export const invoiceCounters = pgTable("invoice_counters", {
  id: text("id").primaryKey(),                         // z.B. "2026" (Jahr)
  year: integer("year").notNull(),
  currentNumber: integer("current_number").notNull().default(0),
  // Default: fortlaufende Nummer + Jahr, z.B. 001-2027 (GoBD: lückenlos, jahresweise neu ab 001).
  // Alte Zeilen mit dem historischen Default "RE-{YYYY}-{NNNNN}" werden beim nächsten
  // Zugriff automatisch migriert (siehe server/services/invoice-number-service.ts,
  // resolveStoredFormatPattern). Individuell gesetzte Patterns bleiben unangetastet.
  formatPattern: text("format_pattern").notNull().default("{NNN}-{YYYY}"),
  updatedAt: timestamp("updated_at").notNull(),
})

export type InvoiceCounter = typeof invoiceCounters.$inferSelect;
export type NewInvoiceCounter = typeof invoiceCounters.$inferInsert;

// Sync-Retry-Queue (für Softec-Synchronisation)
export const syncQueue = pgTable("sync_queue", {
  id: text("id").primaryKey(),
  invoiceId: text("invoice_id").notNull(),
  status: text("status").notNull().default("pending"),  // pending | success | failed
  attempts: integer("attempts").notNull().default(0),
  lastAttemptAt: timestamp("last_attempt_at"),
  lastError: text("last_error"),
  nextRetryAt: timestamp("next_retry_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export type SyncQueueEntry = typeof syncQueue.$inferSelect;
export type NewSyncQueueEntry = typeof syncQueue.$inferInsert;

// ============================================
// PostgreSQL Mirror Tables (strukturierte Spiegelung)
// JSON bleibt autoritativ, PG spiegelt mit echten Spalten
// ============================================

// Angebots-Header
export const pgQuotes = pgTable("pg_quotes", {
  id: serial("id").primaryKey(),
  slot: integer("slot").notNull().unique(),
  angebotNr: text("angebot_nr"),
  kunde: text("kunde"),
  anschrift: text("anschrift"),
  adresszusatz: text("adresszusatz"),
  bauvorhaben: text("bauvorhaben"),
  gewerk: text("gewerk"),
  datum: text("datum"),
  auftrag: text("auftrag"),
  lz: text("lz"),
  customerName: text("customer_name"),
  offerNumber: text("offer_number"),
  totalValue: real("total_value"),
  itemCount: integer("item_count"),
  totalPositions: integer("total_positions"),
  originalFilename: text("original_filename"),
  folderId: text("folder_id"),
  displayOrder: bigint("display_order", { mode: "number" }),
  gewerkOrder: jsonb("gewerk_order").$type<string[]>(),
  createdAt: timestamp("created_at").notNull(),
  updatedAt: timestamp("updated_at").notNull(),
});

export type PgQuote = typeof pgQuotes.$inferSelect;
export type NewPgQuote = typeof pgQuotes.$inferInsert;

// Ausgewählte Positionen (mit Mengen)
export const pgQuoteItems = pgTable("pg_quote_items", {
  id: serial("id").primaryKey(),
  quoteSlot: integer("quote_slot").notNull().references(() => pgQuotes.slot, { onDelete: "cascade" }),
  sortOrder: integer("sort_order"),
  itemId: integer("item_id"),
  originalId: text("original_id"),
  positionNumber: text("position_number"),
  name: text("name"),
  beschreibung: text("beschreibung"),
  longText: text("long_text"),
  gewerk: text("gewerk"),
  einheit: text("einheit"),
  preis: real("preis"),
  preisFehlt: boolean("preis_fehlt"),
  menge: real("menge"),
  umsatzsteuer: integer("umsatzsteuer"),
  gewerkNr: text("gewerk_nr"),
  gewerkName: text("gewerk_name"),
  untergewerkNr: text("untergewerk_nr"),
  untergewerkName: text("untergewerk_name"),
  isHeader: boolean("is_header"),
  parentPositionNumber: text("parent_position_number"),
  hierarchyLevel: integer("hierarchy_level"),
});

export type PgQuoteItem = typeof pgQuoteItems.$inferSelect;
export type NewPgQuoteItem = typeof pgQuoteItems.$inferInsert;

// Datenbank-Header
export const pgDatabases = pgTable("pg_databases", {
  id: text("id").primaryKey(),
  name: text("name"),
  type: text("type"),
  status: text("status"),
  folderId: text("folder_id"),
  displayOrder: bigint("display_order", { mode: "number" }),
  projectDate: text("project_date"),
  projectNumber: text("project_number"),
  projectLocation: text("project_location"),
  projectDescription: text("project_description"),
  entryCount: integer("entry_count"),
  totalValue: real("total_value"),
  gewerkCount: integer("gewerk_count"),
  sourceCreatedBy: text("source_created_by"),
  sourceOriginalFilename: text("source_original_filename"),
  tags: jsonb("tags").$type<string[]>(),
  gaebMetadata: jsonb("gaeb_metadata"),
  createdAt: timestamp("created_at").notNull(),
  updatedAt: timestamp("updated_at").notNull(),
});

export type PgDatabase = typeof pgDatabases.$inferSelect;
export type NewPgDatabase = typeof pgDatabases.$inferInsert;

// Datenbank-Einträge
export const pgDatabaseEntries = pgTable("pg_database_entries", {
  id: serial("id").primaryKey(),
  databaseId: text("database_id").notNull().references(() => pgDatabases.id, { onDelete: "cascade" }),
  sortOrder: integer("sort_order"),
  artikelId: text("artikel_id"),
  gewerk: text("gewerk"),
  name: text("name"),
  beschreibung: text("beschreibung"),
  einheit: text("einheit"),
  preis: real("preis"),
  mwst: integer("mwst"),
  umsatzsteuer: integer("umsatzsteuer"),
  menge: real("menge"),
  positionNumber: text("position_number"),
  entryType: text("entry_type"),
  shortText: text("short_text"),
  longText: text("long_text"),
  hierarchyLevel: integer("hierarchy_level"),
  parentId: text("parent_id"),
  ourPrice: real("our_price"),
  ourComment: text("our_comment"),
  originalPrice: real("original_price"),
  originalQuantity: real("original_quantity"),
  gaebOriginalId: text("gaeb_original_id"),
  gaebOriginalRnoPart: text("gaeb_original_rno_part"),
  isBedarfsposition: boolean("is_bedarfsposition"),
});

export type PgDatabaseEntry = typeof pgDatabaseEntries.$inferSelect;
export type NewPgDatabaseEntry = typeof pgDatabaseEntries.$inferInsert;

// Ordner
export const pgFolders = pgTable("pg_folders", {
  id: text("id").primaryKey(),
  name: text("name"),
  type: text("type"),
  displayOrder: bigint("display_order", { mode: "number" }),
  parentId: text("parent_id"),
  createdAt: timestamp("created_at").notNull(),
  updatedAt: timestamp("updated_at").notNull(),
});

export type PgFolder = typeof pgFolders.$inferSelect;
export type NewPgFolder = typeof pgFolders.$inferInsert;

// PDF-Einstellungen
export const pgPdfSettings = pgTable("pg_pdf_settings", {
  key: text("key").primaryKey(),
  companyName: text("company_name"),
  companyInfo: text("company_info"),
  footerLeft: text("footer_left"),
  footerCenter: text("footer_center"),
  footerRight: text("footer_right"),
  agbText: text("agb_text"),
  defaultGreeting: text("default_greeting"),
  fontFamily: text("font_family"),
  primaryColor: text("primary_color"),
  secondaryColor: text("secondary_color"),
  logoFilename: text("logo_filename"),
  logoMimeType: text("logo_mime_type"),
  updatedAt: timestamp("updated_at").notNull(),
});

export type PgPdfSettings = typeof pgPdfSettings.$inferSelect;
export type NewPgPdfSettings = typeof pgPdfSettings.$inferInsert;
